import { FilterX } from 'lucide-react';
import {
  hasActiveTaskFilters,
  NO_TASK_FILTERS,
  offersAChoice,
  type FilterOption,
  type TaskFilterState,
} from '../../../lib/task-filters';
import { useTranslation } from '../../../lib/i18n';
import { Button, OverflowMenu, type OverflowMenuAction } from '../ui';
import styles from './TasksFilterBar.legacy.module.css';

// The Agent Tasks list's filter row: **Space, then Agent** — each a labelled DS picker whose label states
// the current value, plus a Clear that appears once something is narrowed. Same shape as `FilesFilterBar`,
// which is the shipped precedent for a filter row in this product; there is no DS Select, and the
// OverflowMenu-as-picker is the house answer (a hand-rolled picker control is forbidden outright).
//
// Space leads because it is the question this list is most often narrowed by — and because it is the axis
// the URL carries (`/tasks?workspace=<slug>`), so it is the one a user arrives already narrowed on.
//
// The options are **passed in, not derived here**: the host clamps its state against the same two lists
// (`clampTaskFilters`), and a list derived twice is a list that can disagree with itself. What the bar owns
// is the rule that a control with nothing to choose between is not drawn at all (`offersAChoice`) — one
// option names the only thing there is, which the list already says.
export function TasksFilterBar({
  spaces,
  agents,
  filters,
  onChange,
  onBi,
  className,
}: {
  /** The Spaces worth offering — `spacesOf(tasks, workspaces)`. Empty when the host fixes this axis. */
  spaces: FilterOption[];
  /** The agents worth offering — `agentsOf(tasks, team)`. Empty when the host fixes this axis. */
  agents: FilterOption[];
  filters: TaskFilterState;
  onChange: (next: TaskFilterState) => void;
  /** Reports which filter was used, for BI. */
  onBi?: (name: string) => void;
  /** Layout from the enclosing row — the bar owns its chips, not its place on the page. */
  className?: string;
}) {
  const { t } = useTranslation();

  const pick = (name: string, next: TaskFilterState) => {
    onBi?.(name);
    onChange(next);
  };

  const spaceActions: OverflowMenuAction[] = [
    {
      key: 'all',
      label: t('tasks.filter.allSpaces'),
      onSelect: () => pick('filter_space_all', { ...filters, workspaceId: '' }),
    },
    ...spaces.map((space) => ({
      key: space.id,
      label: space.name,
      onSelect: () => pick('filter_space', { ...filters, workspaceId: space.id }),
    })),
  ];

  const agentActions: OverflowMenuAction[] = [
    {
      key: 'all',
      label: t('tasks.filter.allAgents'),
      onSelect: () => pick('filter_agent_all', { ...filters, agentId: '' }),
    },
    ...agents.map((agent) => ({
      key: agent.id,
      label: agent.name,
      onSelect: () => pick('filter_agent', { ...filters, agentId: agent.id }),
    })),
  ];

  // A picker's label carries the current VALUE — the whole point of a labelled control over a glyph. At rest
  // it names the axis in full ("All Spaces"), because on a two-control row that reads as the answer rather
  // than as a prompt.
  const spaceLabel = spaces.find((space) => space.id === filters.workspaceId)?.name ?? t('tasks.filter.allSpaces');
  const agentLabel = agents.find((agent) => agent.id === filters.agentId)?.name ?? t('tasks.filter.allAgents');

  return (
    <div className={className ? `${styles.bar} ${className}` : styles.bar}>
      {offersAChoice(spaces) && (
        <OverflowMenu
          label={spaceLabel}
          ariaLabel={t('packs.section.workspace')}
          actions={spaceActions}
          selectedKey={filters.workspaceId || 'all'}
          onOpen={() => onBi?.('filter_space_open')}
        />
      )}
      {offersAChoice(agents) && (
        <OverflowMenu
          label={agentLabel}
          ariaLabel={t('island.agent')}
          actions={agentActions}
          selectedKey={filters.agentId || 'all'}
          onOpen={() => onBi?.('filter_agent_open')}
        />
      )}
      {hasActiveTaskFilters(filters) && (
        <Button
          variant="text"
          size="small"
          startIcon={<FilterX size={16} aria-hidden />}
          onClick={() => pick('filter_clear', NO_TASK_FILTERS)}
        >
          {t('files.filter.clear')}
        </Button>
      )}
    </div>
  );
}
